import { useState, FC } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@material-ui/core";
import { Search } from "@material-ui/icons";
import { ModelBundle } from "../../../../../generated/graphql";
import { useReactiveVar } from "@apollo/client/react/hooks/useReactiveVar";
import { useDebounce } from "../../../../shared/Utils/OtherOperations/useDebounce";
import { mobileAppModel } from "../../state";
import { DialogHeader, SearchTextField } from "../../../MobileAppModal.style";
import { CloseIconButton } from "../../../../shared/Buttons/CloseIconButton";
import { LoadingProgressOverlay } from "../../../../shared/LoadingProgress/LoadingProgressOverlay";
import { useExperimentsModelScrollFetch } from "../../hooks/ExperimentsModelScrollFetch";

interface ModelsListProps {
  onClose: () => void;
}

const ExperimentModelsListModal: FC<ModelsListProps> = ({ onClose }) => {
  const model = useReactiveVar(mobileAppModel.model);
  const [searchKey, setSearchKey] = useState("");
  const debouncedSearchKey = useDebounce(searchKey.trim(), 1000);

  const { availableModels, loading, error } = useExperimentsModelScrollFetch(
    debouncedSearchKey,
    model?.contentType?.name,
  );

  if (error) {
    mobileAppModel.setError(
      true,
      "Сталася помилка при завантаженні списку експериментів. Спробуйте пізніше",
    );
    return null;
  }

  const handleClose = () => {
    setSearchKey("");
    onClose();
  };

  const handleSelect = (bundle: Partial<ModelBundle>) => {
    if (!bundle?.id) return;
    mobileAppModel.updateExperimentModel({
      bundleId: bundle.id,
    });
    mobileAppModel.setModelBundle(bundle as ModelBundle);
    handleClose();
  };

  return (
    <Dialog
      id="experiment-models-list"
      open
      PaperProps={{ style: { height: 600, width: 500, paddingBottom: 20 } }}
    >
      {loading && <LoadingProgressOverlay />}
      <DialogHeader>
        <DialogTitle>Оберіть експеримент</DialogTitle>
        <CloseIconButton close={handleClose} />
      </DialogHeader>
      <Box px={3} pb={2}>
        <SearchTextField
          fullWidth
          placeholder="Пошук за назвою або номером експеримента"
          value={searchKey}
          inputProps={{ maxLength: 100 }}
          onChange={({ target }) => setSearchKey(target.value)}
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <DialogContent>
        {!loading && !availableModels.length ? (
          <Typography variant="body2" color="textSecondary" align="center">
            За вашим запитом нічого не знайдено
          </Typography>
        ) : (
          <List disablePadding>
            {availableModels.map((bundle) => (
              <ListItem
                key={bundle.id}
                button
                divider
                onClick={() => handleSelect(bundle)}
              >
                <ListItemText primary={bundle.name} secondary={bundle.number} />
              </ListItem>
            ))}
            <div id="experiment-models-list-observer-anchor" />
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ExperimentModelsListModal;
